/* eslint-disable react/jsx-pascal-case */
import React, {useState, useRef, useEffect} from 'react';
import cx from 'classnames';
import {useRouter} from 'next/router';
import {useTranslations, createTranslator} from 'next-intl';
import {Grid, Row, Col} from '@/grid';
import withMainLayout from 'layouts/main';
import {SectionPaddingMdToZero} from '@/section';
import Container from '@/container';
import Link from '@/links';
import {withBoldWeight} from '@/hocs/withFontType';
import {H2, P, PMarginTMd, PNoMargin, PMarginSm} from '@/typography';
import {ListTerms} from '@/ui/lists/list-icons';
import {Table, TBody, TH, TR, TD} from '@/ui/tables';
import {
  withMarginBottomMd,
  withMarginBottomXl,
  withMarginTopDoubleLg,
  withMarginBottomXxl
} from '@/hocs/withSpacing';
import withBreakpoints from '@/hocs/withBreakpoints';
import {withVisibleDesktop993, withHiddenDesktop993} from '@/hocs/withResponsiveUtils';
import Breadcrumbs from '@/ui/breadcrumbs';
import Dropdown from '@/form-fields/dropdown';
import Tabs from '@/ui/tabs/tabs';
import LegalHero from '@/content/legal/hero';
import usePrivacyNoticeTabItems from '@/ui/tabs/tab-items/privacy-notice-tabs';
import legal from 'pages/legal/legal.module.scss';
import {pageMessagesProps} from 'helpers/i18n';
import {parseLegalRichText} from '@/ui/legal/legal-rich-text';
import {
  handlePrint,
  handleDropdown,
  scrollToTopOfPage,
  scrollableDropdownMob
} from '../components/ui/legal/legal.helper';
import shared from '../messages/en/shared.json';
import legalMessages from '../messages/en/legal.json';
import privacy from '../messages/en/privacy.json';

const seoMetas = props => {
  const seoT = createTranslator({locale: props.locale, messages: props.messages});
  return {
    title: seoT('privacy.seo.title'),
    description: seoT('privacy.seo.description'),
    canonical: seoT('privacy.seo.canonical')
  };
};

const List = props => <ListTerms {...props} />;
const BoldSpan = withBoldWeight('span');
const MarginedH2 = withMarginBottomMd(H2);
const DivMarginedBottomXl = withMarginBottomXl('div');
const PriorVersionMargin = withMarginTopDoubleLg('div');
const BreadcrumbsDesktop = withVisibleDesktop993(withMarginBottomXxl('div'));
const HiddenInDesktop = withHiddenDesktop993('div');

const renderListItem = item => {
  if (typeof item === 'string') {
    return <li key={item}>{parseLegalRichText(item)}</li>;
  }
  const richText = parseLegalRichText(item.text);
  const content = item.wrap === 'p' ? <P>{richText}</P> : richText;
  return (
    <li key={item.text}>
      {content}
      {item.subItems ? <List>{item.subItems.map(renderListItem)}</List> : null}
    </li>
  );
};

const renderBlock = (block, index) => {
  switch (block.tag) {
    case 'p':
      return <P key={index}>{parseLegalRichText(block.text)}</P>;
    case 'pMarginTMd':
      return <PMarginTMd key={index}>{parseLegalRichText(block.text)}</PMarginTMd>;
    case 'boldHeading':
      return <BoldSpan key={index}>{block.text}</BoldSpan>;
    case 'list':
      return <List key={index}>{block.items.map(renderListItem)}</List>;
    case 'table':
      return (
        <Table className={legal['legal-table']} key={index}>
          <TBody>
            <TR>
              <TH>{block.headers.personalData}</TH>
              <TH>{block.headers.purposes}</TH>
              <TH>{block.headers.source}</TH>
              <TH>{block.headers.recipients}</TH>
            </TR>
            {block.rows.map(row => (
              <TR key={row.description}>
                <TD>
                  <PMarginSm>{parseLegalRichText(row.category)}</PMarginSm>
                  <P>{row.description}</P>
                </TD>
                <TD>
                  <List>
                    {row.purposes.map(purpose => (
                      <li key={purpose}>{purpose}</li>
                    ))}
                  </List>
                </TD>
                <TD>
                  <List>
                    {row.sources.map(source => (
                      <li key={source}>{source}</li>
                    ))}
                  </List>
                </TD>
                <TD>
                  <List>
                    {row.recipients.map(recipient => (
                      <li key={recipient}>{recipient}</li>
                    ))}
                  </List>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      );
    default:
      return null;
  }
};

const PrivacyNoticePage = () => {
  const router = useRouter();
  const dropdownRef = useRef();
  const [isDropdownFixed, setIsDropdownFixed] = useState(false);
  const t = useTranslations('privacy');
  const legalT = useTranslations('legal');
  const tabItems = usePrivacyNoticeTabItems();
  const tableOfContents = t.raw('tableOfContents');

  const PrivacyPolicy = () => (
    <>
      <MarginedH2>{t('heading')}</MarginedH2>
      <DivMarginedBottomXl>
        <Link onClick={handlePrint} variant="secondary-no-gradient-download" className="no-print">
          {t('downloadLabel')}
        </Link>
      </DivMarginedBottomXl>
      <div className={cx(legal['table-of-contents'], 'no-print')}>
        <ol>
          {tableOfContents.map((item, index) => (
            <li key={index}>
              <Link href={item.urlCopy} variant="green800">
                {item.title}
              </Link>
            </li>
          ))}
        </ol>
      </div>

      {t.raw('introBlocks').map(renderBlock)}

      {t.raw('sections').map(section => (
        <React.Fragment key={section.title}>
          <H2
            className={section.id ? cx(legal['scroll-mg-t'], 'heading-4') : undefined}
            id={section.id || undefined}>
            {section.title}
          </H2>
          {section.blocks.map(renderBlock)}
        </React.Fragment>
      ))}

      <PriorVersionMargin className={legal['legal-content']}>
        <PMarginSm className="heading-6">
          <BoldSpan>{t('lastUpdated')}</BoldSpan>
        </PMarginSm>
        <PNoMargin>
          <BoldSpan>{t('archivedVersionsLabel')}</BoldSpan>
        </PNoMargin>
        {t.raw('archivedVersions').map(version => (
          <Link key={version.href} href={version.href} target="_blank" variant="default">
            {version.text}
          </Link>
        ))}
      </PriorVersionMargin>
    </>
  );
  useEffect(() => {
    scrollableDropdownMob(dropdownRef, setIsDropdownFixed);
  }, []);

  const ResponsiveMenu = withBreakpoints({
    desktop: () => (
      <Tabs
        activeTab="privacy-policy"
        title={legalT('sectionTitles.privacyNotice')}
        tabItems={tabItems}
        router={router}
      />
    ),
    mobile: () => (
      <div
        ref={dropdownRef}
        className={cx(
          isDropdownFixed ? legal['legal-dropdown-fixed'] : legal['legal-dropdown'],
          'no-print'
        )}>
        <Dropdown
          name="privacy-notice"
          defaultValue="privacy-policy"
          variant="green800"
          values={tabItems}
          onChange={item => handleDropdown(item, router)}
        />
      </div>
    )
  });

  return (
    <>
      <LegalHero title={legalT('sectionTitles.privacyNotice')} />
      <SectionPaddingMdToZero>
        <Container>
          <Grid fluid>
            <BreadcrumbsDesktop>
              <Breadcrumbs
                clickableLink={{text: legalT('breadcrumb.legal'), urlCopy: '/legal'}}
                title={legalT('sectionTitles.privacyNotice')}
                activeTab={tabItems.find(item => item.value === 'privacy-policy').text}
              />
            </BreadcrumbsDesktop>
            <Row>
              <Col xs={12} md={12} lg={3}>
                <ResponsiveMenu />
              </Col>
              <Col xs={12} md={12} lgOffset={1} lg={8}>
                <PrivacyPolicy />
              </Col>
            </Row>
          </Grid>
          <HiddenInDesktop>
            <Link variant="go-to-top-arrow" onClick={scrollToTopOfPage} />
          </HiddenInDesktop>
        </Container>
      </SectionPaddingMdToZero>
    </>
  );
};

export default withMainLayout(PrivacyNoticePage, seoMetas);

export const messagesForIntl = {shared, legal: legalMessages, privacy};

export function getStaticProps() {
  return pageMessagesProps(messagesForIntl);
}
